/**
 * مخطط التخزين المحلي (IndexedDB).
 *
 * ملاحظة تصميمية: نستخدم فهارس على الحقول المستخدمة في الفلترة المتكررة
 * (companyId, taskId, occurrenceKey, ...) مع مسح (cursor scan) للفلترة المركّبة.
 * هذا مقبول لحجم بيانات جهاز واحد. عند العمل عبر Supabase تُستخدم فهارس SQL
 * وترقيم من الخادم (انظر docs/DATABASE.md).
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { TableName } from '../types';

export const LOCAL_DB_NAME = 'taskflow-local';
export const LOCAL_DB_VERSION = 4;

/** جداول محلية إضافية لا تظهر في عقد البيانات العام. */
export const LOCAL_ONLY_STORES = [
  'local_accounts',
  'local_sessions',
  'local_meta',
  'file_blobs',
  'sync_queue',
] as const;

export const TABLE_STORES: readonly TableName[] = [
  'profiles',
  'membership_profiles',
  'platform_owners',
  'platform_members',
  'platform_member_permissions',
  'platform_settings',
  'subscription_plans',
  'company_subscriptions',
  'subscription_events',
  'subscription_payments',
  'company_invites',
  'registration_requests',
  'companies',
  'brands',
  'branches',
  'brand_branch_links',
  'task_categories',
  'memberships',
  'membership_placements',
  'membership_roles',
  'membership_access_grants',
  'roles',
  'role_permissions',
  'role_scopes',
  'supervision_relations',
  'task_templates',
  'task_template_assignees',
  'tasks',
  'task_assignees',
  'task_steps',
  'task_status_history',
  'attachments',
  'attachment_deletions',
  'notifications',
  'settings',
  'company_modules',
  'audit_logs',
];

export interface LocalAccount {
  id: string;
  email: string;
  fullName: string;
  passwordHash: string;
  salt: string;
  iterations: number;
  algorithm: 'PBKDF2-SHA256';
  createdAt: string;
  updatedAt: string;
}

export interface LocalSession {
  id: string;
  userId: string;
  email: string;
  createdAt: string;
  expiresAt: string;
}

export interface LocalMeta {
  key: string;
  value: unknown;
  updatedAt: string;
}

export interface FileBlob {
  id: string;
  storagePath: string;
  bytes: ArrayBuffer;
  mimeType: string;
  updatedAt: string;
}

export type SyncQueueOperation =
  | 'task.complete'
  | 'task.approve'
  | 'task.return'
  | 'attachment.upload'
  | 'attachment.delete'
  | 'task.create'
  | 'task.update'
  | 'task.delete'
  | 'task.reopen'
  | 'task.cancel'
  | 'task.decideApproval'
  | 'table.insert'
  | 'table.insertMany'
  | 'table.update'
  | 'table.remove';

export interface SyncQueueEntry {
  id: string;
  operation: SyncQueueOperation;
  payload: unknown;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TaskflowDB extends DBSchema {
  profiles: { key: string; value: Record<string, unknown> & { id: string } };
  membership_profiles: { key: string; value: Record<string, unknown> & { id: string }; indexes: { companyId: string; userId: string } };
  platform_owners: { key: string; value: Record<string, unknown> & { id: string }; indexes: { userId: string } };
  platform_members: { key: string; value: Record<string, unknown> & { id: string }; indexes: { userId: string; status: string } };
  platform_member_permissions: { key: string; value: Record<string, unknown> & { id: string }; indexes: { userId: string } };
  platform_settings: { key: string; value: Record<string, unknown> & { id: string } };
  subscription_plans: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { code: string };
  };
  company_subscriptions: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; planId: string };
  };
  subscription_events: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; subscriptionId: string };
  };
  subscription_payments: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; subscriptionId: string; clientRef: string };
  };
  company_invites: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; tokenHash: string; email: string };
  };
  registration_requests: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { userId: string; requestId: string; userRequest: [string, string] };
  };
  companies: { key: string; value: Record<string, unknown> & { id: string }; indexes: { status: string } };
  brands: { key: string; value: Record<string, unknown> & { id: string }; indexes: { companyId: string } };
  branches: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; brandId: string };
  };
  brand_branch_links: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; brandId: string; branchId: string };
  };
  task_categories: { key: string; value: Record<string, unknown> & { id: string }; indexes: { companyId: string } };
  memberships: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; userId: string };
  };
  membership_placements: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; membershipId: string; scopeId: string };
  };
  membership_roles: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { membershipId: string; roleId: string };
  };
  membership_access_grants: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; membershipId: string };
  };
  roles: { key: string; value: Record<string, unknown> & { id: string }; indexes: { companyId: string } };
  role_permissions: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { roleId: string };
  };
  role_scopes: { key: string; value: Record<string, unknown> & { id: string }; indexes: { roleId: string } };
  supervision_relations: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; supervisorMembershipId: string; subordinateMembershipId: string };
  };
  task_templates: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; status: string };
  };
  task_template_assignees: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { templateId: string; membershipId: string };
  };
  tasks: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: {
      companyId: string;
      templateId: string;
      occurrenceKey: string;
      branchId: string;
      localDate: string;
      status: string;
      categoryId: string;
    };
  };
  task_assignees: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { taskId: string; membershipId: string };
  };
  task_steps: { key: string; value: Record<string, unknown> & { id: string }; indexes: { taskId: string } };
  task_status_history: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { taskId: string };
  };
  attachments: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; taskId: string; uploadState: string; clientRef: string };
  };
  attachment_deletions: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string };
  };
  notifications: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { recipientUserId: string; companyId: string };
  };
  settings: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { scope: [string, string]; key: string };
  };
  company_modules: { key: string; value: Record<string, unknown> & { id: string }; indexes: { companyId: string } };
  audit_logs: {
    key: string;
    value: Record<string, unknown> & { id: string };
    indexes: { companyId: string; createdAt: string };
  };
  local_accounts: { key: string; value: LocalAccount; indexes: { email: string } };
  local_sessions: { key: string; value: LocalSession; indexes: { userId: string } };
  local_meta: { key: string; value: LocalMeta };
  file_blobs: { key: string; value: FileBlob; indexes: { storagePath: string } };
  sync_queue: { key: string; value: SyncQueueEntry; indexes: { operation: string } };
}

export type TaskflowDatabase = IDBPDatabase<TaskflowDB>;

let dbPromise: Promise<TaskflowDatabase> | null = null;

export function getLocalDb(): Promise<TaskflowDatabase> {
  if (!dbPromise) {
    dbPromise = openDB<TaskflowDB>(LOCAL_DB_NAME, LOCAL_DB_VERSION, {
      upgrade(db) {
        for (const store of TABLE_STORES) {
          if (db.objectStoreNames.contains(store)) continue;
          const created = db.createObjectStore(store, { keyPath: 'id' });
          addIndexes(store, created as unknown as IndexCreator);
        }
        if (!db.objectStoreNames.contains('local_accounts')) {
          const accounts = db.createObjectStore('local_accounts', { keyPath: 'id' });
          accounts.createIndex('email', 'email', { unique: true });
        }
        if (!db.objectStoreNames.contains('local_sessions')) {
          const sessions = db.createObjectStore('local_sessions', { keyPath: 'id' });
          sessions.createIndex('userId', 'userId');
        }
        if (!db.objectStoreNames.contains('local_meta')) {
          db.createObjectStore('local_meta', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('file_blobs')) {
          const blobs = db.createObjectStore('file_blobs', { keyPath: 'id' });
          blobs.createIndex('storagePath', 'storagePath', { unique: true });
        }
        if (!db.objectStoreNames.contains('sync_queue')) {
          const queue = db.createObjectStore('sync_queue', { keyPath: 'id' });
          queue.createIndex('operation', 'operation');
        }
      },
    }).then(async (db) => {
      // ترحيل أمامي غير هدّام: ينشئ نسخة مستقلة لكل عضوية، ولا يعدل الملف العام.
      const tx = db.transaction(['memberships', 'profiles', 'membership_profiles', 'platform_owners', 'companies', 'brands', 'branches'], 'readwrite');
      const [memberships, profiles, owners, companies, brands, branches] = await Promise.all([
        tx.objectStore('memberships').getAll(),
        tx.objectStore('profiles').getAll(),
        tx.objectStore('platform_owners').getAll(),
        tx.objectStore('companies').getAll(),
        tx.objectStore('brands').getAll(),
        tx.objectStore('branches').getAll(),
      ]);
      const profileByUser = new Map(profiles.map((profile) => [profile.id, profile]));
      const platformOwnerIds = new Set(owners.filter((owner) => !owner.revokedAt).map((owner) => owner.userId));
      const target = tx.objectStore('membership_profiles');
      for (const company of companies) {
        if (!Object.prototype.hasOwnProperty.call(company, 'phone')) {
          await tx.objectStore('companies').put({ ...company, phone: null });
        }
      }
      for (const brand of brands) {
        if (!Object.prototype.hasOwnProperty.call(brand, 'phone')) {
          await tx.objectStore('brands').put({ ...brand, phone: null });
        }
      }
      for (const branch of branches) {
        if (!Object.prototype.hasOwnProperty.call(branch, 'phone') || !Object.prototype.hasOwnProperty.call(branch, 'logoAttachmentId') || !Object.prototype.hasOwnProperty.call(branch, 'inheritBrandLogo')) {
          await tx.objectStore('branches').put({
            ...branch,
            phone: branch.phone ?? null,
            logoAttachmentId: branch.logoAttachmentId ?? null,
            inheritBrandLogo: branch.inheritBrandLogo ?? false,
          });
        }
      }
      for (const membership of memberships) {
        if (platformOwnerIds.has(String(membership.userId)) && membership.status === 'active') {
          await tx.objectStore('memberships').put({ ...membership, status: 'ended', endedAt: membership.endedAt ?? new Date().toISOString(), isCompanyOwner: false, updatedAt: new Date().toISOString() });
        }
        if (platformOwnerIds.has(String(membership.userId))) continue;
        if (await target.get(membership.id)) continue;
        const source = profileByUser.get(String(membership.userId));
        await target.put({
          id: membership.id,
          membershipId: membership.id,
          userId: membership.userId,
          companyId: membership.companyId,
          fullName: source?.fullName ?? 'عضو المنشأة',
          phone: source?.phone ?? null,
          avatarDataUrl: source?.avatarDataUrl ?? null,
          createdAt: membership.createdAt,
          updatedAt: membership.updatedAt,
        });
      }
      await tx.done;
      return db;
    });
  }
  return dbPromise;
}

interface IndexCreator {
  createIndex(name: string, keyPath: string | string[], options?: { unique?: boolean }): void;
}

function addIndexes(store: TableName, objectStore: IndexCreator): void {
  switch (store) {
    case 'platform_owners':
      objectStore.createIndex('userId', 'userId');
      break;
    case 'membership_profiles':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('userId', 'userId');
      break;
    case 'platform_members':
      objectStore.createIndex('userId', 'userId', { unique: true });
      objectStore.createIndex('status', 'status');
      break;
    case 'platform_member_permissions':
      objectStore.createIndex('userId', 'userId');
      break;
    case 'subscription_plans':
      objectStore.createIndex('code', 'code', { unique: true });
      break;
    case 'company_subscriptions':
      objectStore.createIndex('companyId', 'companyId', { unique: true });
      objectStore.createIndex('planId', 'planId');
      break;
    case 'subscription_events':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('subscriptionId', 'subscriptionId');
      break;
    case 'subscription_payments':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('subscriptionId', 'subscriptionId');
      objectStore.createIndex('clientRef', 'clientRef');
      break;
    case 'company_invites':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('tokenHash', 'tokenHash', { unique: true });
      objectStore.createIndex('email', 'email');
      break;
    case 'registration_requests':
      objectStore.createIndex('userId', 'userId');
      objectStore.createIndex('requestId', 'requestId');
      objectStore.createIndex('userRequest', ['userId', 'requestId'], { unique: true });
      break;
    case 'companies':
      objectStore.createIndex('status', 'status');
      break;
    case 'brands':
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'branches':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('brandId', 'brandId');
      break;
    case 'brand_branch_links':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('brandId', 'brandId');
      objectStore.createIndex('branchId', 'branchId');
      break;
    case 'task_categories':
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'memberships':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('userId', 'userId');
      break;
    case 'membership_placements':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('membershipId', 'membershipId');
      objectStore.createIndex('scopeId', 'scopeId');
      break;
    case 'membership_roles':
      objectStore.createIndex('membershipId', 'membershipId');
      objectStore.createIndex('roleId', 'roleId');
      break;
    case 'membership_access_grants':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('membershipId', 'membershipId');
      break;
    case 'roles':
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'role_permissions':
      objectStore.createIndex('roleId', 'roleId');
      break;
    case 'role_scopes':
      objectStore.createIndex('roleId', 'roleId');
      break;
    case 'supervision_relations':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('supervisorMembershipId', 'supervisorMembershipId');
      objectStore.createIndex('subordinateMembershipId', 'subordinateMembershipId');
      break;
    case 'task_templates':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('status', 'status');
      break;
    case 'task_template_assignees':
      objectStore.createIndex('templateId', 'templateId');
      objectStore.createIndex('membershipId', 'membershipId');
      break;
    case 'tasks':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('templateId', 'templateId');
      // مفتاح منع التكرار: نفس القالب ونفس الموعد لا يُنشئ سجلين
      objectStore.createIndex('occurrenceKey', 'occurrenceKey', { unique: true });
      objectStore.createIndex('branchId', 'branchId');
      objectStore.createIndex('localDate', 'localDate');
      objectStore.createIndex('status', 'status');
      objectStore.createIndex('categoryId', 'categoryId');
      break;
    case 'task_assignees':
      objectStore.createIndex('taskId', 'taskId');
      objectStore.createIndex('membershipId', 'membershipId');
      break;
    case 'task_steps':
      objectStore.createIndex('taskId', 'taskId');
      break;
    case 'task_status_history':
      objectStore.createIndex('taskId', 'taskId');
      break;
    case 'attachments':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('taskId', 'taskId');
      objectStore.createIndex('uploadState', 'uploadState');
      objectStore.createIndex('clientRef', 'clientRef');
      break;
    case 'attachment_deletions':
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'notifications':
      objectStore.createIndex('recipientUserId', 'recipientUserId');
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'settings':
      objectStore.createIndex('scope', ['scopeType', 'scopeId']);
      objectStore.createIndex('key', 'key');
      break;
    case 'company_modules':
      objectStore.createIndex('companyId', 'companyId');
      break;
    case 'audit_logs':
      objectStore.createIndex('companyId', 'companyId');
      objectStore.createIndex('createdAt', 'createdAt');
      break;
    default:
      break;
  }
}

/** مسح كل بيانات التطبيق المحلية (يُستخدم عند تسجيل الخروج أو إعادة التهيئة). */
export async function clearAllLocalData(options: { keepAccounts?: boolean } = {}): Promise<void> {
  const db = await getLocalDb();
  const tx = db.transaction([...TABLE_STORES, ...LOCAL_ONLY_STORES], 'readwrite');
  const stores: Array<TableName | (typeof LOCAL_ONLY_STORES)[number]> = options.keepAccounts
    ? [...TABLE_STORES, 'file_blobs', 'sync_queue', 'local_meta']
    : [...TABLE_STORES, ...LOCAL_ONLY_STORES];
  await Promise.all(stores.map((store) => tx.objectStore(store).clear()));
  await tx.done;
}

/** للاختبارات: إعادة ضبط الاتصال بقاعدة البيانات */
export function resetLocalDbHandle(): void {
  dbPromise = null;
}
